import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { useFormikContext } from "formik";
import AWSAutomaticAuthentication from "archive/components/aws/AWSAutomaticAuthentication";
import { FormikFormGroup } from "components/common";
import { S3_PROVIDER_COPY } from "archive/logic/s3CompatibleProviders";
import AWSAuthenticationTypeFormGroup from "./aws/AWSAuthenticationTypeFormGroup";
import AWSKeySecretFieldGroup from "./aws/AWSKeySecretFieldGroup";
const AuthWrapper = styled.div(
  ({ theme }) => css`
    margin: 40px 0 21px 30px;
    padding: 15px 0 3px 15px;
    border-left: 3px solid ${theme.colors.gray[80]};
    overflow: hidden;
  `
);
const KEYSECRET_AUTH_TYPE = "keysecret";
const AUTOMATIC_AUTH_TYPE = "automatic";
const DEFAULT_SPOOL_DIRECTORY = "/tmp/graylog-archive-spool";
const ArchiveBackendS3FieldGroup = ({ enableSpool = true, variant = "s3" }) => {
  const {
    values: { id, settings },
    setFieldValue
  } = useFormikContext();
  const copy = S3_PROVIDER_COPY[variant];
  const s3Settings = settings;
  const currentAuth = s3Settings.aws_access_key_id ? KEYSECRET_AUTH_TYPE : AUTOMATIC_AUTH_TYPE;
  const [authenticationType, setAuthenticationType] = useState(currentAuth);
  const isAccesskeySet = s3Settings.aws_secret_access_key && "is_set" in s3Settings.aws_secret_access_key;
  const isType = (type) => authenticationType === type;
  const setAccessKey = useCallback(
    (nextAccessKey) => {
      setFieldValue("settings.aws_secret_access_key", nextAccessKey);
    },
    [setFieldValue]
  );
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;
    if (s3Settings.aws_secret_access_key && "is_set" in s3Settings.aws_secret_access_key) {
      const isSet = s3Settings.aws_secret_access_key.is_set;
      setFieldValue("settings.aws_secret_access_key", isSet ? { keep_value: true } : void 0);
    }
    if (!id) {
      if (enableSpool) {
        setFieldValue("settings.spool_directory", DEFAULT_SPOOL_DIRECTORY);
      }
      setFieldValue("settings.output_path", "");
    }
  }, [setFieldValue, id, s3Settings.aws_secret_access_key, enableSpool]);
  const _handleAuthenticationTypeChange = (option) => {
    if (option === AUTOMATIC_AUTH_TYPE) {
      setFieldValue("settings.aws_access_key_id", void 0);
      setFieldValue("settings.aws_secret_access_key", void 0);
    }
    setAuthenticationType(option);
  };
  const handleAccessKeyChange = useCallback(
    (typedKey) => {
      const nextKey = typedKey === "" ? { delete_value: true } : { set_value: typedKey };
      setAccessKey(nextKey);
    },
    [setAccessKey]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.endpoint_uri",
      type: "text",
      label: copy.endpointLabel,
      help: copy.endpointHelp,
      labelClassName: "",
      wrapperClassName: ""
    }
  ), /* @__PURE__ */ React.createElement(
    AWSAuthenticationTypeFormGroup,
    {
      updateAuthenticationType: _handleAuthenticationTypeChange,
      currentType: authenticationType,
      variant
    }
  ), isType(AUTOMATIC_AUTH_TYPE) && /* @__PURE__ */ React.createElement(AuthWrapper, null, /* @__PURE__ */ React.createElement(AWSAutomaticAuthentication, null)), isType(KEYSECRET_AUTH_TYPE) && /* @__PURE__ */ React.createElement(AuthWrapper, null, /* @__PURE__ */ React.createElement(
    AWSKeySecretFieldGroup,
    {
      isAccesskeySet,
      handleAccessKeyChange,
      variant
    }
  )), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.aws_assume_role_arn",
      type: "text",
      label: copy.assumeRoleLabel,
      placeholder: "arn:aws:sts::123456789012:assumed-role/some-role",
      help: copy.assumeRoleHelp,
      labelClassName: "",
      wrapperClassName: ""
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.aws_external_id",
      type: "text",
      label: "AWS External ID (optional)",
      help: "External ID for the assume role, used to prevent the confused deputy problem. Only applies when an assume role ARN is provided.",
      labelClassName: "",
      wrapperClassName: ""
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.bucket",
      type: "text",
      label: copy.bucketLabel,
      help: copy.bucketHelp,
      labelClassName: "",
      required: true,
      wrapperClassName: "",
      disabled: !!id
    }
  ), enableSpool && /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "settings.spool_directory",
      type: "text",
      label: "Spool directory",
      help: "Spool directory for this backend.",
      labelClassName: "",
      required: true,
      wrapperClassName: ""
    }
  ));
};
export default ArchiveBackendS3FieldGroup;
